import React, { useState, useEffect } from 'react';
import { X, MessageSquareText, Syringe, Stethoscope, CalendarCheck, CalendarX, Wallet, DoorOpen, Cake, Info, PawPrint } from 'lucide-react';
import { getCustomerSmsPreference, updateCustomerSmsPreference } from '../services/api';
import { toast } from 'sonner';
import PetIcon from './PetIcon';

const SMS_CATEGORIES = [
  { key: 'sms_vaccine_reminder', label: 'Aşı Hatırlatma', icon: Syringe, color: 'text-[#34C759]' },
  { key: 'sms_treatment_reminder', label: 'Tedavi Hatırlatma', icon: Stethoscope, color: 'text-[#6b6de3]' },
  { key: 'sms_appointment_confirmed', label: 'Randevu Onayı', icon: CalendarCheck, color: 'text-[#007AFF]' },
  { key: 'sms_appointment_rejected', label: 'Randevu Reddi', icon: CalendarX, color: 'text-[#FF3B30]' },
  { key: 'sms_balance_reminder', label: 'Bakiye Hatırlatma', icon: Wallet, color: 'text-[#FF9500]' },
  { key: 'sms_boarding_checkout', label: 'Konaklama Bildirimi', icon: DoorOpen, color: 'text-[#AF52DE]' },
  { key: 'sms_birthday_greeting', label: 'Doğum Günü', icon: Cake, color: 'text-[#FF2D55]' },
];

const CustomerSmsModal = ({ open, onClose, customer }) => {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const pets = customer?.pets || [];

  useEffect(() => {
    if (open && customer?.id) {
      setLoading(true);
      getCustomerSmsPreference(customer.id)
        .then(r => setPrefs(r.data))
        .catch(() => toast.error('SMS tercihleri yüklenemedi'))
        .finally(() => setLoading(false));
    }
  }, [open, customer?.id]);

  const handleToggleAll = () => {
    const newVal = !prefs?.sms_enabled;
    setPrefs(prev => {
      const updated = { ...prev, sms_enabled: newVal };
      SMS_CATEGORIES.forEach(c => { updated[c.key] = newVal; });
      return updated;
    });
  };

  const handleToggle = (key) => {
    setPrefs(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleTogglePet = (petId) => {
    setPrefs(prev => {
      const disabled = new Set(prev.sms_disabled_pets || []);
      if (disabled.has(petId)) {
        disabled.delete(petId);
      } else {
        disabled.add(petId);
      }
      return { ...prev, sms_disabled_pets: [...disabled] };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateCustomerSmsPreference(customer.id, prefs);
      toast.success('SMS tercihleri kaydedildi');
      onClose();
    } catch {
      toast.error('Kaydetme başarısız');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const disabledPets = new Set(prefs?.sms_disabled_pets || []);

  return (
    <div className="fixed inset-x-0 top-0 bottom-[65px] z-[9998] flex sm:inset-0 sm:items-center sm:justify-center sm:p-3">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full sm:max-w-md lg:max-w-3xl bg-white sm:rounded-2xl shadow-2xl flex flex-col h-full sm:h-auto sm:max-h-[75vh] overflow-hidden" data-testid="customer-sms-modal">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-2">
            <MessageSquareText size={18} className="text-[#6b6de3]" />
            <div>
              <h3 className="text-sm font-bold text-gray-900">SMS Tercihleri</h3>
              <p className="text-[10px] text-[#8E8E93]">{customer?.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-gray-100" data-testid="close-sms-modal">
            <X size={18} className="text-[#8E8E93]" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading ? (
            <p className="text-center text-sm text-[#8E8E93] py-4">Yükleniyor...</p>
          ) : (
            <>
              {/* Master Toggle */}
              <div className="flex items-center justify-between bg-[#F2F2F7] rounded-2xl px-4 py-3">
                <div>
                  <p className="text-sm font-semibold text-gray-900">Tüm SMS Bildirimleri</p>
                  <p className="text-[10px] text-[#8E8E93]">Tüm SMS bildirimlerini aç/kapat</p>
                </div>
                <button
                  onClick={handleToggleAll}
                  className={`w-12 h-7 rounded-full transition-colors relative ${prefs?.sms_enabled ? 'bg-[#34C759]' : 'bg-gray-300'}`}
                  data-testid="toggle-sms-all"
                >
                  <div className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${prefs?.sms_enabled ? 'left-[22px]' : 'left-0.5'}`} />
                </button>
              </div>

              {/* Info */}
              <div className="flex items-start gap-2 bg-blue-50 rounded-xl px-3 py-2">
                <Info size={12} className="text-[#007AFF] mt-0.5 flex-shrink-0" />
                <p className="text-[10px] text-[#007AFF]/80">Genel ayarlarda kapalı olan kategoriler burada açılsa bile gönderilmez.</p>
              </div>

              {/* Per-category toggles */}
              <div className="space-y-1">
                {SMS_CATEGORIES.map(cat => {
                  const Icon = cat.icon;
                  const isOn = prefs?.[cat.key] !== false;
                  return (
                    <div key={cat.key} className="flex items-center justify-between px-3 py-2.5 hover:bg-gray-50 rounded-xl transition-colors">
                      <div className="flex items-center gap-2.5">
                        <Icon size={16} className={cat.color} />
                        <span className="text-xs font-medium text-gray-800">{cat.label}</span>
                      </div>
                      <button
                        onClick={() => handleToggle(cat.key)}
                        className={`w-10 h-6 rounded-full transition-colors relative ${isOn ? 'bg-[#34C759]' : 'bg-gray-200'}`}
                        data-testid={`toggle-${cat.key}`}
                      >
                        <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${isOn ? 'left-[18px]' : 'left-0.5'}`} />
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Per-pet toggles */}
              {pets.length > 0 && (
                <div className="pt-2 border-t border-gray-100">
                  <div className="flex items-center gap-2 mb-2">
                    <PawPrint size={14} className="text-[#8E8E93]" />
                    <span className="text-xs font-semibold text-[#8E8E93] uppercase">Hasta Bazlı SMS</span>
                  </div>
                  <div className="flex items-start gap-2 bg-amber-50 rounded-xl px-3 py-2 mb-2">
                    <Info size={12} className="text-amber-600 mt-0.5 flex-shrink-0" />
                    <p className="text-[10px] text-amber-700/80">Kapatılan hastalar için hiçbir SMS gönderilmez.</p>
                  </div>
                  <div className="space-y-1">
                    {pets.map(pet => {
                      const isEnabled = !disabledPets.has(pet.id);
                      return (
                        <div key={pet.id} className="flex items-center justify-between px-3 py-2.5 hover:bg-gray-50 rounded-xl transition-colors">
                          <div className="flex items-center gap-2.5">
                            <PetIcon species={pet.species} gender={pet.gender} size={16} />
                            <span className="text-xs font-medium text-gray-800">{pet.name}</span>
                          </div>
                          <button
                            onClick={() => handleTogglePet(pet.id)}
                            className={`w-10 h-6 rounded-full transition-colors relative ${isEnabled ? 'bg-[#34C759]' : 'bg-gray-200'}`}
                            data-testid={`toggle-pet-${pet.id}`}
                          >
                            <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${isEnabled ? 'left-[18px]' : 'left-0.5'}`} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-100 shrink-0">
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="w-full bg-[#6b6de3] text-white py-2.5 rounded-xl font-semibold text-sm hover:bg-[#5b5dd3] transition-colors disabled:opacity-50"
            data-testid="save-customer-sms"
          >
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomerSmsModal;
